import { spaces } from '@kimuno/theme';
import CloseIcon from '@mui/icons-material/Close';
import {
	Box,
	Button,
	type ButtonOwnProps,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	IconButton,
	type SxProps,
	type Theme,
	Tooltip,
	Typography,
} from '@mui/material';
import type React from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

interface OwnProps {
	title: string;
	/** A string, or a translated run with markup (a bold name, say). */
	message: React.ReactNode;
	isOpen: boolean;
	confirmButtonColor?: ButtonOwnProps['color'];
	onCloseModal: () => void;
	onConfirm: () => void;
	confirmButtonText?: string;
	cancelButtonText?: string;
	dialogSx?: SxProps<Theme>;
	confirmButtonSx?: SxProps<Theme>;
	isConfirmLoading?: boolean;
	showCloseButton?: boolean;
}

// Top-aligned with the title taking the free space, so a long title wraps
// beside the close button instead of pushing it down.
const titleRowStyles = {
	display: 'flex',
	alignItems: 'flex-start',
	gap: spaces.xs,
};

const titleStyles = { flex: 1 };

const ConfirmationDialog: React.FC<OwnProps> = (props) => {
	const { t } = useTranslation();
	const {
		title,
		message,
		isOpen,
		onCloseModal,
		onConfirm,
		confirmButtonText,
		confirmButtonColor = 'primary',
		cancelButtonText,
		dialogSx,
		confirmButtonSx,
		isConfirmLoading = false,
		showCloseButton = false,
	} = props;
	const titleId = useId();

	return (
		<Dialog
			open={isOpen}
			aria-labelledby={titleId}
			onClose={isConfirmLoading ? undefined : onCloseModal}
			sx={{
				'& .MuiDialog-paper': {
					width: '100%',
				},
				...dialogSx,
			}}
		>
			<DialogContent sx={{ p: 0 }}>
				<Box
					sx={{
						padding: spaces.lg,
						display: 'flex',
						flexDirection: 'column',
						gap: spaces.xs,
					}}
				>
					<Box sx={titleRowStyles}>
						<Typography id={titleId} variant="h5" color="text.primary" sx={titleStyles}>
							{title}
						</Typography>
						{showCloseButton && (
							<Tooltip title={t('Close')}>
								{/* A disabled button fires no events, so the tooltip needs a wrapper to anchor to. */}
								<span>
									<IconButton
										color="default"
										size="small"
										onClick={onCloseModal}
										disabled={isConfirmLoading}
										aria-label={t('Close')}
										data-testid="confirmation-dialog-close-button"
									>
										<CloseIcon />
									</IconButton>
								</span>
							</Tooltip>
						)}
					</Box>
					<Typography variant="subtitle1" color="text.secondary">
						{message}
					</Typography>
				</Box>
			</DialogContent>
			<DialogActions sx={{ padding: spaces.lg }}>
				<Button
					onClick={onCloseModal}
					variant="text"
					color="secondary"
					size="medium"
					disabled={isConfirmLoading}
					data-testid="confirmation-dialog-cancel-button"
				>
					{cancelButtonText || t('Cancel')}
				</Button>
				<Button
					onClick={onConfirm}
					variant="contained"
					color={confirmButtonColor}
					size="medium"
					disabled={isConfirmLoading}
					startIcon={isConfirmLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
					sx={confirmButtonSx}
					data-testid="confirmation-dialog-confirm-button"
				>
					{confirmButtonText || t('Confirm')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ConfirmationDialog;
